/**
 * 患者のログインの画面の部品。既存の患者の画面（routes/patient/PatientPasswordChange.tsx、components/auth/PasswordChangeForm.tsx）の
 * 見出し・入力欄・丸いボタンに寄せる。淡い地の上の文字は WCAG の AA（4.5:1）に届く濃さにする。
 */
import {
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  forwardRef,
  useEffect,
  useRef,
} from "react";

import { PasswordInput } from "../../components/auth/PasswordInput";

import { patientManualUrl } from "./patientAuthConfig";

export const PageLayout = ({ children }: { children: ReactNode }) => (
  <div className="w-full flex justify-center">
    <div className="w-full max-w-patientContent p-5">{children}</div>
  </div>
);

/**
 * 段の見出し。focusOnMount なら、出したときに見出しへフォーカスを移す（段が切り替わったことを読み上げで伝え、
 * キーボードの位置を新しい段の先頭に置く。前の段のフォームが消えるとフォーカスが body に落ちるため）。
 */
export const Heading = ({
  children,
  focusOnMount = false,
}: {
  children: ReactNode;
  focusOnMount?: boolean;
}) => {
  const ref = useRef<HTMLHeadingElement>(null);
  const shouldFocus = useRef(focusOnMount);
  useEffect(() => {
    if (shouldFocus.current) ref.current?.focus();
  }, []);
  return (
    <h1
      ref={ref}
      tabIndex={focusOnMount ? -1 : undefined}
      className="text-xl font-bold mb-4 text-brandBlue focus:outline-none"
    >
      {children}
    </h1>
  );
};

/** 案内。見出しの上に置き、role は付けない。 */
export const InfoMessage = ({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) => (
  <div className="mb-4 rounded-md border border-[#c7d0ee] bg-[#eef1fa] px-3 py-2.5 text-sm text-gray-800">
    {title && <strong className="block mb-1 text-brandBlue">{title}</strong>}
    {children}
  </div>
);

export const SuccessMessage = ({
  children,
  role,
}: {
  children: ReactNode;
  role?: "status";
}) => (
  <div
    role={role}
    className="mb-4 rounded-md border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-800"
  >
    {children}
  </div>
);

/** エラー。見出しの直下に置き、role="alert" を付ける。 */
export const ErrorMessage = ({
  id,
  children,
}: {
  id?: string;
  children: ReactNode;
}) => (
  <div
    id={id}
    role="alert"
    className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
  >
    {children}
  </div>
);

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  id: string;
  label: string;
  help?: string;
  error?: string;
  /** 欄の外にあるエラー（見出しの直下のエラー）の id */
  extraDescribedBy?: string;
  inputClassName?: string;
  /** パスワードの欄にする（表示切り替えのボタン付き）。type は指定しない */
  password?: boolean;
};

/** label を htmlFor で欄に結び、案内とエラーを aria-describedby で結ぶ。 */
export const Field = forwardRef(function Field(
  {
    id,
    label,
    help,
    error,
    extraDescribedBy,
    inputClassName,
    password = false,
    ...input
  }: FieldProps,
  ref: Ref<HTMLInputElement>,
) {
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [helpId, errorId, extraDescribedBy].filter(Boolean).join(" ") || undefined;
  const inputClass = `w-full border border-gray-300 rounded-md px-3 py-2 text-base placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandBlue ${inputClassName ?? ""}`;
  return (
    <div className="mb-4">
      <label className="block text-sm text-gray-800 mb-2" htmlFor={id}>
        {label}
      </label>
      {password ? (
        <PasswordInput
          {...input}
          revealable
          // 患者の画面は、サーバーへ送っている間は欄を disabled にするので、そのときに隠す（画面の検証で止まったときは隠さない）
          sending={Boolean(input.disabled)}
          id={id}
          ref={ref}
          aria-describedby={describedBy}
          aria-invalid={error || extraDescribedBy ? true : undefined}
          className={inputClass}
        />
      ) : (
        <input
          {...input}
          id={id}
          ref={ref}
          aria-describedby={describedBy}
          aria-invalid={error || extraDescribedBy ? true : undefined}
          className={inputClass}
        />
      )}
      {help && (
        <p
          id={helpId}
          className="mt-1 text-xs text-gray-600 whitespace-pre-line"
        >
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
});

type ButtonProps = {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "solid" | "outline";
};

export const BlockButton = forwardRef(function BlockButton(
  {
    children,
    disabled,
    onClick,
    type = "button",
    variant = "solid",
  }: ButtonProps,
  ref: Ref<HTMLButtonElement>,
) {
  const look =
    variant === "solid"
      ? "text-white bg-brandBlue"
      : "text-brandBlue bg-white border border-brandBlue";
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`w-full inline-flex items-center justify-center rounded-full px-5 py-2.5 disabled:opacity-50 ${look}`}
    >
      {children}
    </button>
  );
});

export const Actions = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-3 mt-2">{children}</div>
);

export const Divider = () => <hr className="my-5 border-gray-300" />;

export const Muted = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-gray-600">{children}</p>
);

/** 窓口の案内（Q49）。手引きの URL が無ければ出さない。 */
export const SupportContact = () => {
  const url = patientManualUrl();
  if (!url) return null;
  return (
    <p
      className="mt-2 border-t border-dashed border-gray-300 pt-2 text-sm text-gray-600"
      data-testid="patient-support-contact"
    >
      お問い合わせ窓口の連絡先は、
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brandBlue underline"
      >
        ご利用の手引き
        <span className="sr-only">（別のタブで開きます）</span>
      </a>
      をご覧ください。
    </p>
  );
};
